/**
 * Verwandte §§ aus LEXCORE norm_chain (cross_refs) → ParagraphRef für die Suchleiste.
 * Kein Korpus im Browser — nur ID→Label-Mapping.
 */

import type { LexProof, LexProofNormChainEntry } from './lexProof';
import type { ParagraphRef } from './portalParagraphRef';

const RELATED_MIN = 3;
const RELATED_MAX = 8;

/** LEXCORE-IDs typisch `MRG-16`, `ABGB-1096`, `DSGVO-6`. */
const NORM_ID_RE = /^([A-Za-zÄÖÜäöü][A-Za-z0-9ÄÖÜäöü]*)-(\d{1,4}[a-z]?(?:\.\d+)?)$/i;

export function normIdToParagraphRef(normId: string): ParagraphRef | null {
  const raw = String(normId || '').trim();
  if (!raw) return null;
  const m = raw.match(NORM_ID_RE);
  if (!m) return null;
  const law = m[1].toUpperCase();
  const section = m[2];
  return {
    section,
    law,
    label: `§ ${section} ${law}`,
    key: `${law}:${section}`.toLowerCase(),
  };
}

function isCrossRefVia(via?: string): boolean {
  const v = String(via || '');
  return v.startsWith('cross_ref:') || v === 'related' || v === 'cross_ref';
}

/**
 * 3–8 verwandte §§ aus lexProof.norm_chain (nur Cross-Refs, keine Primary-Hits).
 * excludeKeys: bereits angezeigte/aktive Treffer (z. B. aktueller Lookup).
 */
export function relatedParagraphRefsFromNormChain(
  chain: LexProofNormChainEntry[] | undefined | null,
  opts?: { excludeKeys?: Iterable<string>; min?: number; max?: number },
): ParagraphRef[] {
  const max = opts?.max ?? RELATED_MAX;
  const exclude = new Set(
    [...(opts?.excludeKeys ?? [])].map((k) => String(k).toLowerCase()),
  );
  const out: ParagraphRef[] = [];
  const seen = new Set<string>();

  const sorted = [...(chain ?? [])].sort((a, b) => {
    const ra = typeof a.rank === 'number' ? a.rank : 999;
    const rb = typeof b.rank === 'number' ? b.rank : 999;
    return ra - rb;
  });

  for (const entry of sorted) {
    if (!isCrossRefVia(entry.via)) continue;
    const ref = normIdToParagraphRef(entry.norm_id);
    if (!ref || exclude.has(ref.key) || seen.has(ref.key)) continue;
    seen.add(ref.key);
    out.push(ref);
    if (out.length >= max) break;
  }

  // Unter RELATED_MIN trotzdem anzeigen, wenn es welche gibt (kein Auffüllen).
  void (opts?.min ?? RELATED_MIN);
  return out;
}

export function relatedParagraphRefsFromLexProof(
  proof: LexProof | null | undefined,
  opts?: { excludeKeys?: Iterable<string>; min?: number; max?: number },
): ParagraphRef[] {
  if (!proof?.norm_chain?.length) return [];
  const primaryKeys = new Set<string>();
  for (const s of proof.sources ?? []) {
    const r = normIdToParagraphRef(s.norm_id);
    if (r) primaryKeys.add(r.key);
  }
  for (const e of proof.norm_chain) {
    if (!isCrossRefVia(e.via)) {
      const r = normIdToParagraphRef(e.norm_id);
      if (r) primaryKeys.add(r.key);
    }
  }
  const exclude = new Set([...(opts?.excludeKeys ?? []), ...primaryKeys]);
  return relatedParagraphRefsFromNormChain(proof.norm_chain, { ...opts, excludeKeys: exclude });
}
